import React, { useRef, useEffect, useState } from 'react'
import { useGetTagsQuery } from '../../services/tagApi'
import { useAppSelector } from '../../store/hooks'
import { selectSelectedProjectId } from '../../store/slices/projectSlice'
import { AnnotationDataBoundingBox } from '../../types/models/annotation'
import { useGetProjectQuery } from '../../services/projectsApi'

interface ContentImageCardProps {
  base64Data: string | undefined
  bounding_boxes: AnnotationDataBoundingBox[]
  contentWidth: number
  contentHeight: number
  showConfidence?: boolean
  showBoundingBoxes?: boolean
  background?: string
  isSelected?: boolean
  isHovered?: boolean
}

const ContentImageCard: React.FC<ContentImageCardProps> = ({
  base64Data,
  bounding_boxes,
  contentWidth,
  contentHeight,
  showConfidence = true,
  showBoundingBoxes = true,
  background,
  isSelected = false,
  isHovered = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [imageDisplayDimensions, setImageDisplayDimensions] = useState({
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  })
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  useEffect(() => {
    if (!containerRef.current) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        setDimensions({ width, height })
      }
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  // Calculate actual image display dimensions when container or image changes
  useEffect(() => {
    if (!imageRef.current || !dimensions.width || !dimensions.height) return

    const containerAspectRatio = dimensions.width / dimensions.height
    const imageAspectRatio = contentWidth / contentHeight
    let displayWidth, displayHeight, x, y

    if (containerAspectRatio > imageAspectRatio) {
      // Container is wider than image
      displayHeight = dimensions.height
      displayWidth = dimensions.height * imageAspectRatio
      x = (dimensions.width - displayWidth) / 2
      y = 0
    } else {
      // Container is taller than image
      displayWidth = dimensions.width
      displayHeight = dimensions.width / imageAspectRatio
      x = 0
      y = (dimensions.height - displayHeight) / 2
    }

    setImageDisplayDimensions({
      width: displayWidth,
      height: displayHeight,
      x,
      y,
    })
  }, [dimensions, contentWidth, contentHeight])

  const { data: tags } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  const getTagColor = (box: AnnotationDataBoundingBox) => {
    const tag = tags?.tags?.find((tag) => tag.id === box.name || tag.name === box.class_name)
    if (tag) {
      return `#${tag.color.toString(16).padStart(6, '0').slice(0, 6)}`
    } else {
      return '#800085'
    }
  }

  if (!base64Data) {
    return null
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Background Layer */}
      {background && imageDisplayDimensions.width > 0 && (
        <div
          className="image-background"
          style={{
            position: 'absolute',
            width: `${imageDisplayDimensions.width}px`,
            height: `${imageDisplayDimensions.height}px`,
            left: `${imageDisplayDimensions.x}px`,
            top: `${imageDisplayDimensions.y}px`,
            background: background,
            borderRadius: '8px',
            zIndex: 1,
          }}
        />
      )}

      {/* Hover/Selection Overlay */}
      {(isHovered || isSelected) && imageDisplayDimensions.width > 0 && (
        <div
          style={{
            position: 'absolute',
            width: `${imageDisplayDimensions.width}px`,
            height: `${imageDisplayDimensions.height}px`,
            left: `${imageDisplayDimensions.x}px`,
            top: `${imageDisplayDimensions.y}px`,
            border: isSelected ? '2px solid #9333ea' : '2px solid #007bff',
            borderRadius: '8px',
            zIndex: 10,
            boxShadow: isSelected ? '0 4px 15px rgba(147, 51, 234, 0.3)' : 'none',
            transition: 'all 0.3s ease',
            boxSizing: 'border-box',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Image Layer */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          transformOrigin: '0 0',
          zIndex: 2,
        }}
      >
        <img
          ref={imageRef}
          src={base64Data}
          alt="Annotated"
          style={{
            position: 'absolute',
            width: `${imageDisplayDimensions.width}px`,
            height: `${imageDisplayDimensions.height}px`,
            left: `${imageDisplayDimensions.x}px`,
            top: `${imageDisplayDimensions.y}px`,
          }}
        />
      </div>

      {/* Bounding Box Overlay Layer */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          transition: 'transform 0.3s ease-in-out',
          transformOrigin: '0 0',
          pointerEvents: 'none',
          zIndex: 10,
        }}
      >
        {imageDisplayDimensions.width > 0 &&
          showBoundingBoxes &&
          bounding_boxes.map((box, index) => {
            const borderWidth = 1
            const color = getTagColor(box)

            // Check if box has points array (new format) or use legacy min/max
            if (box.points && box.points.length === 8) {
              // After API transform, points are in content pixel space (using content.width/content.height from API)
              // Map directly from content pixels to display thumbnail pixels

              // Detect if points look normalized (shouldn't happen after API transform, but handle for safety)
              const maxX = Math.max(box.points[0], box.points[2], box.points[4], box.points[6])
              const maxY = Math.max(box.points[1], box.points[3], box.points[5], box.points[7])
              const areNormalized = maxX <= 1 && maxY <= 1 && (maxX > 0 || maxY > 0)

              // Convert to content pixels if normalized, otherwise use as-is
              const contentPixels: [number, number][] = []

              if (areNormalized) {
                // Normalized [0,1] → content pixels
                for (let i = 0; i < 8; i += 2) {
                  contentPixels.push([
                    box.points[i] * contentWidth,
                    box.points[i + 1] * contentHeight,
                  ])
                }
              } else {
                // Points are already in content pixel space (from API transform)
                for (let i = 0; i < 8; i += 2) {
                  contentPixels.push([box.points[i], box.points[i + 1]])
                }
              }

              // Map from content pixels to display thumbnail pixels
              const points: [number, number][] = contentPixels.map(([cx, cy]) => {
                const px =
                  imageDisplayDimensions.x + (cx / contentWidth) * imageDisplayDimensions.width
                const py =
                  imageDisplayDimensions.y + (cy / contentHeight) * imageDisplayDimensions.height
                return [px, py]
              })

              // Create SVG path for oriented bounding box
              const pathData = `M ${points[0][0]} ${points[0][1]} L ${points[1][0]} ${points[1][1]} L ${points[2][0]} ${points[2][1]} L ${points[3][0]} ${points[3][1]} Z`

              // Calculate position for confidence label (use first point)
              const labelX = points[0][0]
              const labelY = Math.max(imageDisplayDimensions.y, points[0][1] - 20)

              return (
                <div key={index}>
                  <svg
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'none',
                    }}
                  >
                    <path d={pathData} fill="none" stroke={color} strokeWidth={borderWidth} />
                  </svg>
                  {showConfidence && typeof box.confidence === 'number' && (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${labelX}px`,
                        top: `${labelY}px`,
                        background: color,
                        color: '#fff',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        zIndex: 20,
                      }}
                    >
                      {box.class_name}: {(box.confidence * 100).toFixed(1)}%
                    </div>
                  )}
                </div>
              )
            } else {
              // Legacy format: xmin/ymin/xmax/ymax
              // After API transform, these are in content pixel space
              let xmin = box?.xmin ?? 0
              let xmax = box?.xmax ?? 0
              let ymin = box?.ymin ?? 0
              let ymax = box?.ymax ?? 0

              // Detect if normalized (shouldn't happen after API transform, but handle for safety)
              const maybeNormalized = xmax <= 1 && ymax <= 1 && (xmax > 0 || ymax > 0)
              if (maybeNormalized) {
                xmin = xmin * contentWidth
                xmax = xmax * contentWidth
                ymin = ymin * contentHeight
                ymax = ymax * contentHeight
              }

              // Map from content pixels to display thumbnail pixels
              const xPos =
                imageDisplayDimensions.x + (xmin / contentWidth) * imageDisplayDimensions.width
              const yPos =
                imageDisplayDimensions.y + (ymin / contentHeight) * imageDisplayDimensions.height
              const width = ((xmax - xmin) * imageDisplayDimensions.width) / contentWidth
              const height = ((ymax - ymin) * imageDisplayDimensions.height) / contentHeight

              return (
                <div key={index}>
                  <div
                    style={{
                      position: 'absolute',
                      left: `${xPos}px`,
                      top: `${yPos}px`,
                      width: `${width}px`,
                      height: `${height}px`,
                      border: `${borderWidth}px solid ${color}`,
                      boxSizing: 'border-box',
                      pointerEvents: 'none',
                    }}
                  />
                  {showConfidence && typeof box.confidence === 'number' && (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${xPos}px`,
                        top: `${Math.max(imageDisplayDimensions.y, yPos - 20)}px`,
                        background: color,
                        color: '#fff',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        zIndex: 20,
                      }}
                    >
                      {box.class_name}: {(box.confidence * 100).toFixed(1)}%
                    </div>
                  )}
                </div>
              )
            }
          })}
      </div>
    </div>
  )
}

export default ContentImageCard
